// QuickActions — global "+" launcher mounted once in RootLayout. Every action
// REUSES an existing compliant flow (reason codes, capability gates, audit,
// approval routing unchanged); this component only decides which actions the
// current role may launch and supplies the store context. When "All Stores"
// is active, stock-touching flows first ask for an explicit store — never
// silently act on a default one.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuickActionsFab, type QuickActionItem } from '@/foundation/QuickActionsFab';
import { Button, Field, Modal, Select } from '@/foundation/primitives';
import { useStore } from '@/store/store';
import { useDisplayStores } from '@/api/useStores';
import { GlobalAdjustFlow } from '@/features/inventory/GlobalAdjustFlow';
import { NewAdjustmentModal } from '@/features/inventory/NewAdjustmentModal';
import { AddStockFlow } from '@/features/stocks/inward/AddStockFlow';
import { quickActionsBus, type QuickActionId } from './quickActionsBus';

/** Flows that write stock need a concrete store; drafts pick their own. */
const NEEDS_STORE: QuickActionId[] = ['adjust', 'add-stock'];

export function QuickActions() {
  const navigate = useNavigate();
  const { store, can } = useStore();
  const stores = useDisplayStores();
  const [flow, setFlow] = useState<QuickActionId | null>(null);
  // Store chosen for this flow when the global context is "All Stores".
  const [flowStore, setFlowStore] = useState<string>('');

  // The ⌘K palette (and anything else) can open a flow through the bus.
  useEffect(() => quickActionsBus.subscribe((id) => setFlow(id)), []);

  const openFlow = (id: QuickActionId) => {
    setFlowStore('');
    setFlow(id);
  };
  const close = () => {
    setFlow(null);
    setFlowStore('');
  };

  const items: QuickActionItem[] = [
    {
      id: 'adjust',
      label: 'Adjust stock',
      caption: 'Correct a batch quantity — applied immediately, audited',
      icon: 'sliders',
      lockMsg: can('stk.adjustment.approve') ? undefined : 'Only Pharmacist / IM can direct-adjust',
      onSelect: () => openFlow('adjust'),
    },
    {
      id: 'add-stock',
      label: 'Add stock',
      caption: 'Receive a product without a GRN (direct import)',
      icon: 'plus',
      lockMsg: can('stk.direct_import.add') ? undefined : 'Your role cannot add stock',
      onSelect: () => openFlow('add-stock'),
    },
    {
      id: 'new-adjustment',
      label: 'New adjustment (approval)',
      caption: 'Draft a correction for Pharmacist / IM sign-off',
      icon: 'adjustments',
      lockMsg: can('adjustment.draft') ? undefined : 'Your role cannot draft adjustments',
      onSelect: () => openFlow('new-adjustment'),
    },
    {
      id: 'receive-delivery',
      label: 'Receive delivery (GRN)',
      caption: 'Open Stock Inward for a supplier delivery',
      icon: 'truck',
      lockMsg: can('stk.inward.create') ? undefined : 'Your role cannot receive goods',
      onSelect: () => navigate('/stocks/inward'),
    },
  ];

  // Resolve the store the active flow should act in.
  const needsStore = flow !== null && NEEDS_STORE.includes(flow);
  const resolvedStore = store !== 'ALL' ? store : flowStore;

  return (
    <>
      <QuickActionsFab items={items} />

      {/* Explicit store choice when the global context is "All Stores". */}
      {needsStore && !resolvedStore && (
        <StoreSelectModal stores={stores} onPick={setFlowStore} onClose={close} />
      )}

      {flow === 'adjust' && resolvedStore && (
        <GlobalAdjustFlow storeId={resolvedStore} onClose={close} />
      )}
      {flow === 'add-stock' && resolvedStore && (
        <AddStockFlow storeId={resolvedStore} onClose={close} />
      )}
      {/* Self-contained: derives its store from the picked batch (All-Stores safe). Mounted only
          while open — it loads the batches, products and bins, and this shell is on every page. */}
      {flow === 'new-adjustment' && <NewAdjustmentModal open onClose={close} />}
    </>
  );
}

function StoreSelectModal({
  stores,
  onPick,
  onClose,
}: {
  stores: { id: string; name: string }[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState('');
  return (
    <Modal
      open
      onClose={onClose}
      width={400}
      title="Which store?"
      subtitle="You're viewing All Stores — stock changes apply to one store."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Field label="Store" required>
          <Select
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            placeholder="Select store…"
            options={stores.map((s) => ({ value: s.id, label: s.name }))}
          />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="check" disabled={!choice} onClick={() => onPick(choice)}>
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
